import { botApi } from './api'
import { chartPush } from './chartPush'
import { applyCommitToSaved, parseWorkbenchState } from '@/components/Workbench/workbenchState'

/**
 * The saved workbench blob's reads and writes, in order, per bot.
 *
 * The server keeps one blob per bot and a save replaces it whole (last write wins). Two writers
 * share it within a tab: the Workbench's autosave, and — for a Send to AI that lands while the
 * Workbench is unmounted (the Backtest / Optimise tabs, another strategy) — persistCommit below.
 * Queuing every read and write per bot keeps them from interleaving: an unmount flush lands
 * before the off-tab commit reads the blob, and a remount's restore reads it after the commit
 * was written. Across browser tabs it stays last-write-wins, as before.
 */

/** A request that never answers must not stall the bot's saves for good. */
const WAIT_MS = 15_000

const chains = new Map<string, Promise<void>>()

function after(prev: Promise<void>): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, WAIT_MS)
    void prev.then(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

function enqueue<T>(bid: string, task: () => Promise<T>): Promise<T> {
  const prev = chains.get(bid) ?? Promise.resolve()
  const run = after(prev).then(task)
  const settled = run.then(
    () => undefined,
    () => undefined,
  )
  chains.set(bid, settled)
  void settled.then(() => {
    if (chains.get(bid) === settled) chains.delete(bid)
  })
  return run
}

export const workbenchPersist = {
  get(bid: string): Promise<{ state: Record<string, unknown> | null }> {
    return enqueue(bid, () => botApi.getWorkbench(bid))
  },

  save(bid: string, state: Record<string, unknown>): Promise<void> {
    return enqueue(bid, () => botApi.saveWorkbench(bid, state))
  },

  /**
   * Fold the bot's last commit into its saved blob — for a send the Workbench was not mounted
   * to persist. Reads the commit and what is staged when its turn comes, so the newest wins. A
   * bot with nothing saved has nothing to fold into: its Workbench builds the base when it runs.
   */
  persistCommit(bid: string): Promise<void> {
    return enqueue(bid, async () => {
      const commit = chartPush.get(bid).lastCommit
      if (!commit) return
      const saved = parseWorkbenchState((await botApi.getWorkbench(bid))?.state)
      if (!saved) return
      const next = applyCommitToSaved(saved, commit, chartPush.get(bid).staged)
      await botApi.saveWorkbench(bid, next as unknown as Record<string, unknown>)
    })
  },
}
